import React from 'react';
import { HandCoins, GraduationCap, HeartHandshake, LifeBuoy, Clock, Sprout } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from './ui/drawer';
import { useFlag } from '../lib/flags';
import { centerToast } from './CenterToast';

const EntryQuickLog = React.lazy(() => import('../features/tracker/EntryQuickLog'));

/**
 * EntrySheet — what the raised "+" in the bottom bar opens.
 *
 * Two halves. "Log today" is the daily habit the tracker will own; until that
 * ships it is a single invitation card. "Do something" is the occasional,
 * higher-intent action, and every row here lands exactly where the equivalent
 * card on Home lands — no new destinations, no new slugs.
 *
 * A daily log must never cost more than two taps from anywhere in the app.
 * That is the whole reason this sheet exists rather than a sixth tab.
 */

const TODAY_FORMAT = { weekday: 'short', day: 'numeric', month: 'short' };

export default function EntrySheet({ isOpen, onClose, onNavigate, onOpenHub, onOpenTracker }) {
  const trackerOn = useFlag('tracker');

  const today = new Date().toLocaleDateString('en-SG', TODAY_FORMAT);

  // Every action routes through the shell's existing navigation: Home, opened
  // on the hub the matching card already lives in.
  const go = (hub) => {
    onClose?.();
    onOpenHub?.(hub);
  };

  const openTracker = () => {
    onClose?.();
    if (!trackerOn) {
      centerToast('Tracker is coming soon', '🌱');
      return;
    }
    onOpenTracker?.();
  };

  const actions = [
    { id: 'give', label: 'Give', icon: HandCoins, run: () => go('give') },
    { id: 'learn', label: 'Join a class', icon: GraduationCap, run: () => go('learn') },
    { id: 'volunteer', label: 'Volunteer', icon: HeartHandshake, run: () => go('volunteer') },
    { id: 'help', label: 'Request help', icon: LifeBuoy, run: () => go('services') },
    {
      id: 'hours',
      label: 'Log volunteer hours',
      icon: Clock,
      run: () => {
        onClose?.();
        onNavigate?.('volunteer-hours');
      },
    },
  ];

  return (
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DrawerContent
        className="mx-auto max-w-[480px]"
        data-testid="entry-sheet"
      >
        <DrawerHeader className="text-left pb-2">
          <div className="flex items-baseline justify-between gap-3">
            <DrawerTitle>Log today</DrawerTitle>
            <span className="text-xs font-medium text-muted-foreground">{today}</span>
          </div>
          <DrawerDescription className="sr-only">
            Log a habit, or choose something to do.
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-2">
          {trackerOn ? (
            <React.Suspense fallback={<div className="h-20 rounded-2xl bg-muted/40 animate-pulse" />}>
              <EntryQuickLog onAddTracker={openTracker} onClose={onClose} />
            </React.Suspense>
          ) : (
            <button
              type="button"
              data-testid="entry-tracker-empty"
              onClick={openTracker}
              className="w-full rounded-2xl border border-border bg-muted/30 p-4 text-left flex items-start gap-3 transition-colors hover:bg-muted/50"
            >
              <span className="h-10 w-10 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                <Sprout className="h-5 w-5 text-[#0F4C4A]" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-foreground">
                  Track what matters to you.
                </span>
                <span className="block text-xs text-muted-foreground leading-relaxed mt-0.5">
                  Small, steady steps. Start with one.
                </span>
              </span>
            </button>
          )}
        </div>

        <div className="px-4 pt-3 pb-8">
          <p className="text-[12px] uppercase tracking-wider font-semibold text-muted-foreground mb-2.5">
            Do something
          </p>
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  type="button"
                  data-testid={`entry-action-${action.id}`}
                  onClick={action.run}
                  className="h-10 pl-3 pr-4 rounded-full border border-border bg-white text-sm font-semibold text-foreground flex items-center gap-2 transition-colors hover:bg-muted active:scale-[0.98]"
                >
                  <Icon className="h-4 w-4 text-[#0F4C4A]" />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
